import { useLazyQuery } from '@apollo/client';
import debounce from 'lodash/debounce';
import { CheckIcon, LoaderCircleIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  HStack,
  Text,
  VStack,
  tv,
  useComboboxInputValue,
  useSetComboboxInputValue,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const searchTenantsQuery = graphql(`
  query searchTenants($filter: TenantsBoolExp, $limit: Int) {
    tenants(where: $filter, limit: $limit, orderBy: { name: ASC }) {
      __typename
      id
      name
    }
  }
`);

type Tenant = {
  id: string;
  name: string;
};

interface TenantSelectorProps {
  disabled?: boolean;
  selectedTenant?: Tenant | null;
  onTenantSelected: (tenant: Tenant) => void;
  placeholder?: string;
}

const selectTenantVariant = tv({
  base: 'w-full px-4 hover:bg-bg-quaternary',
  variants: {
    disabled: {
      true: 'pointer-events-none opacity-50',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

const TenantComboboxContent: React.FC<{
  selectedTenant?: Tenant | null;
  searchResults: Tenant[];
  loading: boolean;
  hasSearched: boolean;
  onTenantSelected: (tenant: Tenant) => void;
  debouncedSearch: ReturnType<typeof debounce>;
  setSearchResults: React.Dispatch<React.SetStateAction<Tenant[]>>;
}> = ({
  selectedTenant,
  searchResults,
  loading,
  hasSearched,
  onTenantSelected,
  debouncedSearch,
  setSearchResults,
}) => {
  const inputValue = useComboboxInputValue();
  const setInputValue = useSetComboboxInputValue();
  const prevInputRef = useRef(inputValue);

  useEffect(
    function searchOnInputChange() {
      if (prevInputRef.current !== inputValue) {
        prevInputRef.current = inputValue;

        if (!inputValue || inputValue.length < 2) {
          setSearchResults([]);
        } else {
          debouncedSearch(inputValue);
        }
      }
    },
    [inputValue, debouncedSearch, setSearchResults],
  );

  useEffect(
    function searchOnMount() {
      if (inputValue && inputValue.length >= 2) {
        debouncedSearch(inputValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      {loading && (
        <VStack align="center" justify="center" wFull={false} className="py-4">
          <LoaderCircleIcon className="size-6 animate-spin text-bg-accent-strong-primary" />
          <Text size="sm" color="secondary" className="mt-2">
            Searching tenants...
          </Text>
        </VStack>
      )}

      {!loading && inputValue.length > 0 && inputValue.length < 2 && (
        <Text size="sm" color="tertiary" className="px-4 py-2">
          Type at least 2 characters to search
        </Text>
      )}

      {!loading &&
        hasSearched &&
        inputValue.length >= 2 &&
        searchResults.length === 0 && (
          <Text size="sm" color="tertiary" className="px-4 py-2">
            No tenants found
          </Text>
        )}

      {!loading &&
        searchResults.length > 0 &&
        searchResults.map((tenant) => (
          <ComboboxOption
            key={tenant.id}
            onSelect={() => {
              onTenantSelected(tenant);
              setInputValue('');
              setSearchResults([]);
            }}
          >
            <HStack justify="between">
              <VStack gap={0}>
                <Text>{tenant.name}</Text>
                <Text size="sm" color="tertiary">
                  {tenant.id}
                </Text>
              </VStack>
              {selectedTenant?.id === tenant.id && (
                <CheckIcon className="w-max text-bg-accent-strong-primary" />
              )}
            </HStack>
          </ComboboxOption>
        ))}
    </>
  );
};

export const TenantSelector: React.FC<TenantSelectorProps> = ({
  disabled = false,
  selectedTenant,
  onTenantSelected,
  placeholder = 'Select Tenant',
}) => {
  const [searchResults, setSearchResults] = useState<Tenant[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchTenants, { loading }] = useLazyQuery(searchTenantsQuery);

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (!query || query.length < 2) {
          setSearchResults([]);
          return;
        }

        searchTenants({
          variables: {
            filter: { name: { _ilike: `%${query}%` } },
            limit: 25,
          },
        }).then((result) => {
          const tenants = (result.data?.tenants ?? []).map((t) => ({
            id: t.id,
            name: t.name,
          }));
          setSearchResults(tenants);
          setHasSearched(true);
        });
      }, 250),
    [searchTenants],
  );

  useEffect(
    function cancelDebounceOnUnmount() {
      return () => debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  return (
    <Combobox disabled={disabled}>
      <ComboboxSelectButton className={selectTenantVariant({ disabled })}>
        {selectedTenant ? (
          <span className="text-text-secondary">{selectedTenant.name}</span>
        ) : (
          <span className="text-text-tertiary">{placeholder}</span>
        )}
      </ComboboxSelectButton>
      <ComboboxDropdown
        className="pointer-events-auto"
        searchPlaceholder="Search by tenant name"
        shouldFilter={false}
      >
        <TenantComboboxContent
          selectedTenant={selectedTenant}
          searchResults={searchResults}
          loading={loading}
          hasSearched={hasSearched}
          onTenantSelected={onTenantSelected}
          debouncedSearch={debouncedSearch}
          setSearchResults={setSearchResults}
        />
      </ComboboxDropdown>
    </Combobox>
  );
};
